import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Truck, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import Modal from '../../components/ui/Modal';
import StatusChip from '../../components/ui/StatusChip';

const statusIcon = { initiated: Clock, in_transit: Truck, delivered: CheckCircle };
const statusColor = {
  initiated:  'text-zip-muted',
  in_transit: 'text-blue-600',
  delivered:  'text-emerald-600',
};

const DirectDeliveryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [delivery, setDelivery]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: 'in_transit', note: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchDelivery = () => {
    api.get(`/direct-delivery/${id}`)
      .then((r) => setDelivery(r.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDelivery(); }, [id]);

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.patch(`/direct-delivery/${id}/status`, statusForm);
      toast.success('Status updated');
      setShowModal(false);
      fetchDelivery();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="space-y-4">
      {[...Array(2)].map((_, i) => <div key={i} className="zip-card h-40 skeleton" />)}
    </div>
  );
  if (!delivery) return <div className="text-center text-zip-muted py-24">Delivery not found</div>;

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/direct-delivery')}
          className="flex items-center gap-2 text-sm text-zip-muted hover:text-zip-text transition-colors"
        >
          <ArrowLeft size={15} /> Back to Deliveries
        </button>
        {delivery.status !== 'delivered' && (
          <button onClick={() => setShowModal(true)} className="btn-primary">
            Update Status
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Items */}
          <div className="zip-card overflow-hidden">
            <div className="px-5 py-4 border-b border-zip-border">
              <h3 className="font-semibold text-zip-text tracking-tight">Items</h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="bg-zip-surface border-b border-zip-border">
                  {['Item', 'Qty', 'Unit'].map((h) => <th key={h} className="zip-th">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {delivery.items.map((item, i) => (
                  <tr key={i} className="border-b border-zip-border last:border-0">
                    <td className="zip-td font-medium">{item.itemName}</td>
                    <td className="zip-td font-mono">{item.quantity}</td>
                    <td className="zip-td font-mono text-zip-muted">{item.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Timeline */}
          <div className="zip-card p-5">
            <h3 className="font-semibold text-zip-text tracking-tight mb-5">Chain of Custody</h3>
            <div className="relative pl-6">
              <div className="absolute left-2.5 top-0 bottom-0 w-px bg-zip-border" />
              {delivery.statusHistory?.map((h, i) => {
                const Icon = statusIcon[h.status] ?? Clock;
                const isLast = i === delivery.statusHistory.length - 1;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.07 }}
                    className="relative mb-5 last:mb-0"
                  >
                    <div className={`absolute -left-6 w-5 h-5 rounded-full flex items-center justify-center border-2
                      ${isLast ? 'border-zip-red bg-zip-card' : 'border-zip-border bg-zip-card'}`}>
                      <Icon size={10} className={isLast ? 'text-zip-red' : 'text-zip-muted'} />
                    </div>
                    <div className={`ml-2 p-3 rounded-xl border
                      ${isLast ? 'bg-zip-red/5 border-zip-red/20' : 'bg-zip-surface border-zip-border'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-semibold text-sm capitalize ${statusColor[h.status] ?? 'text-zip-muted'}`}>
                          {h.status?.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-zip-muted font-mono">
                          {format(new Date(h.timestamp), 'dd MMM yyyy HH:mm')}
                        </span>
                      </div>
                      {h.note && <p className="text-sm text-zip-muted">{h.note}</p>}
                      {h.updatedBy?.name && (
                        <p className="text-xs text-zip-muted mt-1 font-mono">By {h.updatedBy.name}</p>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Info sidebar */}
        <div className="zip-card p-5 space-y-3 h-fit">
          <h3 className="font-semibold text-zip-text tracking-tight border-b border-zip-border pb-3">
            Delivery Info
          </h3>
          {[
            ['Delivery #',   <span className="font-mono text-zip-red text-xs">{delivery.deliveryNumber}</span>],
            ['Status',       <StatusChip status={delivery.status} />],
            ['Delivered To', delivery.deliveredTo?.name],
            ['Type',         <span className={`chip-${delivery.deliveredTo?.type === 'agent' ? 'blue' : 'purple'}`}>{delivery.deliveredTo?.type}</span>],
            delivery.deliveredTo?.contact && ['Contact', delivery.deliveredTo.contact],
            delivery.expectedDeliveryDate && ['Expected', format(new Date(delivery.expectedDeliveryDate), 'dd MMM yyyy')],
            delivery.deliveryDate && ['Delivered', format(new Date(delivery.deliveryDate), 'dd MMM yyyy')],
          ].filter(Boolean).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm">
              <span className="text-zip-muted">{k}</span>
              <span className="text-zip-text font-medium">{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Status update modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Update Delivery Status"
      >
        <form onSubmit={handleUpdateStatus} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">New Status</label>
            <select
              className="zip-input"
              value={statusForm.status}
              onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
            >
              <option value="in_transit">In Transit</option>
              <option value="delivered">Delivered</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Note</label>
            <textarea
              className="zip-input resize-none"
              rows={3}
              value={statusForm.note}
              onChange={(e) => setStatusForm({ ...statusForm, note: e.target.value })}
              placeholder="Status update note…"
            />
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={submitting} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {submitting
                ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Updating…</>
                : 'Update Status'
              }
            </button>
            <button type="button" onClick={() => setShowModal(false)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default DirectDeliveryDetail;
